import { useState, useEffect } from 'react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { usePWA } from './usePWA';
import { useAuth } from '../context/AuthContext';
import { biometricAPI } from '../services/api';

interface BiometricDevice {
  id: number;
  device_name: string;
  created_at: string;
  last_used_at: string;
}

interface BiometricAuthStatus {
  isSupported: boolean;
  hasCredentials: boolean;
  devices: BiometricDevice[];
}

export const useBiometricAuth = () => {
  const { isPWA } = usePWA();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<BiometricAuthStatus>({
    isSupported: false,
    hasCredentials: false,
    devices: []
  });
  const [isLoading, setIsLoading] = useState(false);

  // Verificar soporte de WebAuthn
  const checkSupport = () => {
    const isSupported = !!(
      window.PublicKeyCredential &&
      window.navigator.credentials &&
      typeof window.navigator.credentials.create === 'function' &&
      typeof window.navigator.credentials.get === 'function'
    );

    return isSupported;
  };

  // Cargar estado biométrico
  const loadStatus = async () => {
    if (!isPWA || !isAuthenticated || !checkSupport()) {
      setStatus({
        isSupported: checkSupport(),
        hasCredentials: false,
        devices: []
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Verificar estado
      const statusResponse = await biometricAPI.getStatus();
      const devicesResponse = await biometricAPI.getDevices();
      
      setStatus({
        isSupported: true,
        hasCredentials: statusResponse.data.data.hasCredentials,
        devices: devicesResponse.data.data.devices || []
      });
    } catch (error) {
      console.error('Error cargando estado biométrico:', error);
      setStatus({
        isSupported: true,
        hasCredentials: false,
        devices: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Registrar nueva credencial biométrica
  const registerBiometric = async (deviceName?: string): Promise<boolean> => {
    if (!isPWA || !checkSupport()) {
      console.error('BiometricAuth: PWA or WebAuthn not supported', { isPWA, supported: checkSupport() });
      throw new Error('Autenticación biométrica no soportada');
    }

    try {
      setIsLoading(true);
      console.log('BiometricAuth: Starting registration...');

      // Obtener opciones de registro del servidor
      console.log('BiometricAuth: Getting registration options from server...');
      const beginResponse = await biometricAPI.registerBegin();
      console.log('BiometricAuth: Server response:', beginResponse.data);
      const options = beginResponse.data.data;

      // Convertir challenge de base64url a Uint8Array
      console.log('BiometricAuth: Starting WebAuthn registration...');
      const attResp = await startRegistration(options);
      console.log('BiometricAuth: WebAuthn registration completed');

      // Completar registro en el servidor
      console.log('BiometricAuth: Completing registration on server...');
      await biometricAPI.registerComplete({
        credential: attResp,
        deviceName: deviceName || getDeviceName()
      });
      console.log('BiometricAuth: Registration completed successfully');

      // Recargar estado
      await loadStatus();
      
      return true;
    } catch (error: any) {
      console.error('BiometricAuth: Registration error:', error);
      console.error('BiometricAuth: Error details:', {
        name: error.name,
        message: error.message,
        response: error.response?.data,
        stack: error.stack
      });
      
      if (error.name === 'NotAllowedError') {
        throw new Error('El usuario canceló o no completó el registro biométrico');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Autenticación biométrica no soportada en este dispositivo');
      } else if (error.response?.data?.message) {
        throw new Error(`Error del servidor: ${error.response.data.message}`);
      } else {
        throw new Error(`Error al configurar autenticación biométrica: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Verificar si un email tiene credenciales biométricas (sin autenticación)
  const checkEmailHasCredentials = async (email: string): Promise<boolean> => {
    if (!isPWA || !checkSupport() || !email.trim()) {
      return false;
    }

    try {
      const beginResponse = await biometricAPI.authenticateBegin(email);
      return beginResponse.data.success;
    } catch (error: any) {
      // Si hay error 404, significa que no hay credenciales
      if (error.response?.status === 404) {
        return false;
      }
      console.warn('Error checking credentials for email:', error);
      return false;
    }
  };

  // Autenticar con biometría
  const authenticateWithBiometric = async (email: string): Promise<{ user: any; token: string }> => {
    if (!isPWA || !checkSupport()) {
      throw new Error('Autenticación biométrica no soportada');
    }

    try {
      setIsLoading(true);

      // Obtener opciones de autenticación del servidor
      const beginResponse = await biometricAPI.authenticateBegin(email);
      const options = beginResponse.data.data;

      // Realizar autenticación biométrica
      const authResp = await startAuthentication(options);

      // Completar autenticación en el servidor
      const completeResponse = await biometricAPI.authenticateComplete({
        email,
        credential: authResp
      });

      return completeResponse.data.data;
    } catch (error: any) {
      console.error('Error en autenticación biométrica:', error);
      
      if (error.name === 'NotAllowedError') {
        throw new Error('Autenticación biométrica cancelada');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('No se encontraron credenciales biométricas para este usuario');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Error en autenticación biométrica');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Eliminar dispositivo biométrico
  const removeDevice = async (deviceId: number): Promise<void> => {
    try {
      setIsLoading(true);
      await biometricAPI.removeDevice(deviceId);
      await loadStatus();
    } catch (error: any) {
      console.error('Error eliminando dispositivo:', error);
      throw new Error(error.response?.data?.message || 'Error al eliminar dispositivo');
    } finally {
      setIsLoading(false);
    }
  };

  // Obtener nombre del dispositivo
  const getDeviceName = (): string => {
    const userAgent = navigator.userAgent;
    
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      return 'iPhone/iPad';
    } else if (/Android/.test(userAgent)) {
      return 'Dispositivo Android';
    } else if (/Windows/.test(userAgent)) {
      return 'Windows PC';
    } else if (/Mac/.test(userAgent)) {
      return 'Mac';
    } else {
      return 'Dispositivo';
    }
  };

  // Cargar estado al montar o cuando cambie el contexto
  useEffect(() => {
    loadStatus();
  }, [isPWA, isAuthenticated]);

  return {
    ...status,
    isLoading,
    isAvailable: false, // DISABLED: Face ID deshabilitado temporalmente
    registerBiometric,
    authenticateWithBiometric,
    checkEmailHasCredentials,
    removeDevice,
    refreshStatus: loadStatus
  };
};